import { useMemo, useRef } from 'react'
import { MdFilterList } from 'react-icons/md'
import { Msg, useMsg } from '~/common/localizationUtils'
import { $thm } from '~/common/themeSx'
import { LegacyDateRangePicker, LegacyTimeRangePicker } from '~/components/v2/DatePicker'
import { SavedFilterSection } from '~/components/v2/FiltersSidebar/SavedFilterSection'
import { ResultsLayout } from '~/components/v2/ResultsLayout'
import { useActionBarContext } from '~/components/v2/ResultsLayout/ActionBar/ActionBar.hook'
import { FilterType, useFilter, useFilters, useFiltersClear, useFiltersInitializing } from '~/context/filters'
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  Skeleton,
  Stack,
  insetDivider
} from '~/design_system'
import { SadStates } from '~/components/SadStates'
import { JSFSelect } from '../../jsonschema/VndlyJsonSchemaFormBase/widgets/select'
import { CheckboxList } from '../CheckboxList'
import AsyncCheckboxList from '../CheckboxList/AsyncCheckboxList'
import { VndlyBusinessUnitPickerv2 } from '../VndlyBusinessUnitPicker'

const Filter = ({
  id,
  value,
  onChange,
  type,
  filter: { config, options, optionsFromApi, preloadFromApi, valueLookupFromApi, placeholder }
}) => {
  switch (type) {
    case FilterType.AsyncList:
      return (
        <AsyncCheckboxList id={id} config={config} optionsFromApi={optionsFromApi} onChange={onChange} value={value} />
      )

    case FilterType.List:
      return <CheckboxList config={config} options={options} onChange={onChange} value={value} />

    case FilterType.DateRange:
      return (
        <Stack mb={4} data-testid="date-range-filter">
          <LegacyDateRangePicker onChange={onChange} value={value} shouldWrapChildren={false} />
        </Stack>
      )

    case FilterType.TimeRange:
      return (
        <Stack mb={4}>
          <LegacyTimeRangePicker onChange={onChange} value={value} shouldWrapChildren={false} />
        </Stack>
      )

    case FilterType.BusinessUnit:
      return (
        <VndlyBusinessUnitPickerv2
          isMulti
          value={value}
          onChange={({ value }) => onChange(value.map(v => v.id))}
          placeholderText={placeholder}
          menuPortalTarget={document.body}
          id="filters_sidebar"
        />
      )

    case FilterType.Select:
      return (
        <JSFSelect
          value={value}
          options={{ optionsFromApi, preloadFromApi, valueLookupFromApi }}
          onChange={onChange}
          multiple
        />
      )

    default:
      return null
  }
}

const FilterItem = ({ id }) => {
  const { value, onChange, filter, type } = useFilter(id)
  const FilterToRender = filter.component ?? Filter
  return <FilterToRender filter={filter} type={type} id={id} value={value} onChange={onChange} />
}

const FiltersAccordian = props => {
  const filters = useFilters()
  const defaultIndex = filters.reduce((acc, { defaultIsOpen }, idx) => {
    if (defaultIsOpen) acc.push(idx)
    return acc
  }, [])

  return (
    <Accordion
      data-testid="filters-accordion"
      allowMultiple
      {...props}
      defaultIndex={defaultIndex}
      pos="relative"
      sx={$thm({ pb: { $canvas: 2 } })}>
      {filters
        .filter(({ type }) => type !== 'external')
        .map(({ id, title }) => (
          <AccordionItem
            key={id}
            data-testid={`filter_by_${id}`}
            sx={$thm({
              // Inset the divider, not the item: the panel's collapse wrapper clips its content, so it stays full
              // width to leave room for checkbox hover halos.
              position: { $canvas: 'relative' },
              borderTopWidth: { $canvas: 0 },
              '&:last-of-type': { borderBottomWidth: { $canvas: 0 } },
              '&::before': insetDivider(),
              '&:has(> button:hover)::before': { opacity: 0 },
              '&:has(> button[aria-expanded="false"]:hover) + &::before': { opacity: 0 }
            })}>
            {({ isExpanded }) => <FilterAccordionItem id={id} title={title} isExpanded={isExpanded} />}
          </AccordionItem>
        ))}
    </Accordion>
  )
}

function FilterAccordionItem({ id, title, isExpanded }) {
  const msg = useMsg()
  const titleText = msg(title)

  // Doing some work here to not render a filter until it's expanded, then
  // once it is, we do not rerender it every subsequent time it's expanded.
  // Hoping this helps with perf.
  const wasEverExpanded = useRef()
  const isLoading = useFiltersInitializing()
  useMemo(() => {
    wasEverExpanded.current = wasEverExpanded?.current || isExpanded
  }, [isExpanded])

  return (
    <>
      <AccordionButton
        pos="relative"
        zIndex={1}
        textStyle="bodySmall"
        data-testid={`filter_button_${titleText}`}
        sx={$thm({
          // Hover bleeds 8px past the inset dividers, which line up with the text.
          mx: { $canvas: 2 },
          w: { $canvas: 'calc(100% - var(--chakra-space-4))' },
          px: { $canvas: 2 },
          borderRadius: { $canvas: 'xl' },
          // One shade past the list item hover, which is too faint on the tonal panel.
          _hover: { bg: { $canvas: 'var(--cnvs-base-palette-slate-200)' } }
        })}>
        <Box flex="1" textAlign="left" py={2}>
          {title}
        </Box>
        <AccordionIcon />
      </AccordionButton>
      <AccordionPanel data-testid={`filter_panel_${titleText}`}>
        <SadStates
          states={[
            {
              when: !(wasEverExpanded.current || isExpanded),
              render: () => <></>
            },
            {
              when: isLoading,
              render: () => <Skeleton w="full" h={4} />
            }
          ]}>
          <FilterItem id={id} />
        </SadStates>
      </AccordionPanel>
    </>
  )
}

function ClearButton({ 'data-testid': dataTestId = 'clear-filters-button', ...props }) {
  const clear = useFiltersClear()
  return (
    <Button size="xs" variant="link" onClick={clear} m={0} data-testid={dataTestId} {...props}>
      <Msg id="components.filters.clear" />
    </Button>
  )
}

/**
 * @deprecated Use ListLayout instead which is a higher-level component that configure a whole list page for you.
 * @see ListLayout
 */
export const FiltersSidebar = props => {
  // This component tries to use the ActionBarContext to control open/close drawer state
  // but isOpen and onClose can also just be passed in as props
  const {
    isOpen: isOpenFromProps,
    onClose: onCloseFromProps,
    savedFilterNamespace,
    clearFiltersTestId,
    ...rest
  } = props
  const actionBar = useActionBarContext()
  const isOpen = isOpenFromProps ?? actionBar?.filtersDisclosure?.isOpen
  const onClose = onCloseFromProps ?? actionBar?.filtersDisclosure?.onClose
  return (
    <>
      <ResultsLayout.Sidebar {...rest}>
        <ResultsLayout.SidebarHeader icon={MdFilterList}>
          <ResultsLayout.SidebarHeading>
            <Msg id="components.filters.title" />
          </ResultsLayout.SidebarHeading>
          <ClearButton data-testid={clearFiltersTestId} />
        </ResultsLayout.SidebarHeader>
        <ResultsLayout.SidebarContent spacing={4}>
          {savedFilterNamespace && <SavedFilterSection namespace={savedFilterNamespace} />}
          <FiltersAccordian />
        </ResultsLayout.SidebarContent>
      </ResultsLayout.Sidebar>
      <Drawer
        data-open={isOpen}
        isOpen={isOpen}
        onClose={onClose}
        placement="left"
        size="md"
        display={{ base: 'block', lg: 'none' }}>
        <DrawerContent className="isChakra drawer">
          <DrawerHeader px={4}>
            <Msg id="components.filters.title" description="Filters" />
            <ClearButton data-testid={clearFiltersTestId} ml={6} />
          </DrawerHeader>
          <DrawerCloseButton />
          <DrawerBody overflowY="scroll" px={0}>
            {savedFilterNamespace && <SavedFilterSection namespace={savedFilterNamespace} />}
            <FiltersAccordian />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  )
}
